import { useState, useEffect, useCallback } from "react";
import { WAREHOUSE_COORDINATES } from "../config/warehouse";
import { getRoadDistanceKm } from "../services/distanceService";

const WAREHOUSE_LABEL = "E-freight warehouse (Amersfoort)";

export interface WarehouseToAddressRouteParams {
  /** Delivery address coordinates; when null, no route is computed */
  deliveryAddress: { lat: number; lon: number; formatted?: string } | null;
}

export interface WarehouseToAddressRouteResult {
  routeGeometry: number[][];
  destCoords: { lat: number; lon: number } | null;
  distanceKm: number | null;
  destLabel: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Computes route from warehouse to delivery address for LCL seafreight delivery map.
 * Origin is always WAREHOUSE_COORDINATES; destination is the chosen delivery address.
 * Used only for displaying the map (no cost calculation).
 */
export function useWarehouseToAddressRoute({
  deliveryAddress,
}: WarehouseToAddressRouteParams): WarehouseToAddressRouteResult {
  const [routeGeometry, setRouteGeometry] = useState<number[][]>([]);
  const [destCoords, setDestCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [destLabel, setDestLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compute = useCallback(async () => {
    if (!deliveryAddress) {
      setRouteGeometry([]);
      setDestCoords(null);
      setDistanceKm(null);
      setDestLabel(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getRoadDistanceKm(WAREHOUSE_COORDINATES, {
        lat: deliveryAddress.lat,
        lon: deliveryAddress.lon,
      });
      if (result == null) {
        setDistanceKm(null);
        setRouteGeometry([]);
        setError("Could not calculate route");
      } else {
        setDistanceKm(result.distanceKm);
        setRouteGeometry(result.routeGeometry ?? []);
      }
      setDestCoords({ lat: deliveryAddress.lat, lon: deliveryAddress.lon });
      setDestLabel(deliveryAddress.formatted ?? "Delivery address");
    } catch (err) {
      console.error("useWarehouseToAddressRoute error:", err);
      setDistanceKm(null);
      setRouteGeometry([]);
      setError(err instanceof Error ? err.message : "Route calculation failed");
    } finally {
      setLoading(false);
    }
  }, [deliveryAddress?.lat, deliveryAddress?.lon, deliveryAddress?.formatted]);

  useEffect(() => {
    compute();
  }, [compute]);

  return {
    routeGeometry,
    destCoords,
    distanceKm,
    destLabel,
    loading,
    error,
  };
}

export { WAREHOUSE_COORDINATES, WAREHOUSE_LABEL };
